import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { DroppedReason } from '@tassomutuo-backoffice/types';

// Definizione delle fasi e delle motivazioni associate
const PHASE_MAPPING = {
  'Fase Pre Appuntamento': [
    'Appuntamento Disdetto',
    'Non si è mai presentato all’appuntamento',
  ],
  'Fase Post Appuntamento': [
    'Appuntamento andato male / Requisiti Bocciati',
    'Andato con un’altra offerta / Non vuole pagare la provvigione',
  ],
  'Fase Avanzata': [
    'Dilungamento / Altri Problemi',
    'Pratica Bloccata dalla Banca',
  ],
};

interface IProps {
  isPrinting?: boolean;
  droppedReasons?: DroppedReason;
  benchmarkDroppedReasons?: DroppedReason;
}

// Componente per renderizzare una singola fase
const PhaseSection: React.FC<{
  title: string;
  reasons: Array<{
    label: string;
    value: number;
    benchmarkValue?: number;
    difference?: number;
  }>;
}> = ({ title, reasons }) => (
  <Box sx={{ flex: 1, minWidth: '300px', mb: 4, maxWidth: '400px' }}>
    <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
      {title}
    </Typography>
    {reasons.length > 0 ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {reasons.map((reason) => {
          const hasBenchmark = reason.benchmarkValue !== undefined;
          const isWorseThanBenchmark = hasBenchmark && reason.difference! > 0;
          const isBetterThanBenchmark = hasBenchmark && reason.difference! < 0;

          return (
            <Box key={reason.label} sx={{}}>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {reason.label}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {reason.value.toFixed(1)}%
                </Typography>

                {hasBenchmark && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: isWorseThanBenchmark
                        ? '#F44336'
                        : isBetterThanBenchmark
                        ? '#4CAF50'
                        : '#666',
                      borderRadius: '20px',
                      border: '1px solid rgba(233, 234, 235, 1)',
                      px: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        color: isWorseThanBenchmark
                          ? '#F44336'
                          : isBetterThanBenchmark
                          ? '#4CAF50'
                          : '#666',
                      }}
                    >
                      {reason.difference! > 0 ? '+' : ''}
                      {reason.difference!.toFixed(1)}% rispetto al benchmark
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    ) : (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: 'italic' }}
      >
        Nessun dato disponibile
      </Typography>
    )}
  </Box>
);

const DashboardDropReasonsSummary = ({
  isPrinting,
  droppedReasons,
  benchmarkDroppedReasons,
}: IProps) => {
  // Helper function per trovare il valore benchmark di una motivazione
  const getBenchmarkValue = (label: string): number | undefined => {
    return benchmarkDroppedReasons?.percentages.find(
      (reason) => reason.label === label
    )?.value;
  };

  // Raggruppa i dati per fase usando useMemo per performance
  const groupedData = useMemo(() => {
    const grouped: Record<
      string,
      Array<{
        label: string;
        value: number;
        benchmarkValue?: number;
        difference?: number;
      }>
    > = {};

    // Inizializza ogni fase con un array vuoto
    Object.keys(PHASE_MAPPING).forEach((phase) => {
      grouped[phase] = [];
    });

    // Raggruppa le motivazioni per fase
    droppedReasons?.percentages.forEach((reason) => {
      // Trova la fase a cui appartiene questa motivazione
      const phase = Object.keys(PHASE_MAPPING).find((phaseName) =>
        PHASE_MAPPING[phaseName as keyof typeof PHASE_MAPPING].includes(
          reason.label
        )
      );

      if (phase) {
        const benchmarkValue = getBenchmarkValue(reason.label);
        const difference =
          benchmarkValue !== undefined
            ? reason.value - benchmarkValue
            : undefined;

        grouped[phase].push({
          label: reason.label,
          value: reason.value,
          benchmarkValue,
          difference,
        });
      }
    });

    return grouped;
  }, [droppedReasons?.percentages, benchmarkDroppedReasons]);

  return (
    <Box
      sx={{
        backgroundColor: '#FAFAFA',
        padding: 4,
        mt: 6,
        borderRadius: '16px',
        border: '1px solid gainsboro',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" mb={1} fontWeight="bold">
            Motivazioni di Drop
          </Typography>
          <Typography variant="h6" mb={2}>
            Tutte le motivazione di drop delle lead assegnate ai tuoi consulenti
            confrontate con il benchmark
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: 4,
          flexDirection: isPrinting ? 'column' : 'row', // Layout responsive per stampa
          flexWrap: 'wrap',
        }}
      >
        {Object.entries(groupedData).map(([phase, reasons]) => (
          <PhaseSection key={phase} title={phase} reasons={reasons} />
        ))}
      </Box>
    </Box>
  );
};

export default DashboardDropReasonsSummary;
